import "cesium/Build/Cesium/Widgets/widgets.css";
import {
  Cartesian2,
  Cartesian3,
  Color,
  DistanceDisplayCondition,
  ImageryLayer,
  LabelStyle,
  Math as CesiumMath,
  NearFarScalar,
  OpenStreetMapImageryProvider,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  TileMapServiceImageryProvider,
  Viewer,
  buildModuleUrl,
} from "cesium";
import baseline from "../data/voivodeship-baseline-2025.json";
import assistance from "../data/assistance-priorities.json";
import facilityManifest from "../research/codex-worker/facility-source-manifest.json";
import coverage from "../research/codex-worker/coverage-report.json";
import "./styles.css";

const format = new Intl.NumberFormat("en-US");
const totalChildren = baseline.national.familyCareChildren + baseline.national.institutionalResidents;

const centers = {
  "Dolnośląskie": [16.45, 51.05],
  "Kujawsko-pomorskie": [18.5, 53.05],
  Lubelskie: [22.8, 51.25],
  Lubuskie: [15.5, 52.2],
  Łódzkie: [19.4, 51.6],
  Małopolskie: [20.2, 49.85],
  Mazowieckie: [21.1, 52.25],
  Opolskie: [17.9, 50.65],
  Podkarpackie: [22.2, 49.95],
  Podlaskie: [23.1, 53.2],
  Pomorskie: [18.1, 54.15],
  Śląskie: [19.0, 50.25],
  Świętokrzyskie: [20.75, 50.75],
  "Warmińsko-mazurskie": [20.8, 53.85],
  Wielkopolskie: [17.2, 52.3],
  Zachodniopomorskie: [15.3, 53.5],
};

const measures = {
  total: {
    label: "Children in care",
    color: "#287965",
    value: (region) => region.familyCareChildren + region.institutionalResidents,
  },
  family: {
    label: "Children in family care",
    color: "#3f9279",
    value: (region) => region.familyCareChildren,
  },
  institution: {
    label: "Children in institutional care",
    color: "#c36f62",
    value: (region) => region.institutionalResidents,
  },
  facilities: {
    label: "Institutional facilities",
    color: "#52768a",
    value: (region) => region.institutionalFacilities,
  },
};

const regionKey = (name) => name.toLocaleLowerCase("pl-PL");
const sourceByRegion = new Map(facilityManifest.sources.map((source) => [regionKey(source.voivodeship), source]));
const regionByName = new Map(baseline.regions.map((region) => [region.voivodeship, region]));

const calmBaseLayer = ImageryLayer.fromProviderAsync(
  TileMapServiceImageryProvider.fromUrl(buildModuleUrl("Assets/Textures/NaturalEarthII"), {
    credit: "Natural Earth II",
  }),
);
calmBaseLayer.brightness = 1.08;
calmBaseLayer.contrast = 0.86;
calmBaseLayer.saturation = 0.5;
calmBaseLayer.gamma = 1.02;

const detailedMapProvider = new OpenStreetMapImageryProvider({
  url: "https://tile.openstreetmap.org/",
  maximumLevel: 19,
  credit: "© OpenStreetMap contributors",
});
detailedMapProvider.enablePickFeatures = false;
const detailedMapLayer = new ImageryLayer(detailedMapProvider);
detailedMapLayer.brightness = 1.04;
detailedMapLayer.contrast = 0.92;
detailedMapLayer.saturation = 0.62;
detailedMapLayer.gamma = 1.02;

const viewer = new Viewer("globe", {
  animation: false,
  baseLayer: calmBaseLayer,
  baseLayerPicker: false,
  fullscreenButton: false,
  geocoder: false,
  homeButton: false,
  infoBox: false,
  navigationHelpButton: false,
  requestRenderMode: true,
  sceneModePicker: false,
  selectionIndicator: false,
  timeline: false,
  vrButton: false,
});

viewer.imageryLayers.add(detailedMapLayer);

viewer.scene.backgroundColor = Color.fromCssColorString("#dfe9e4");
viewer.scene.globe.baseColor = Color.fromCssColorString("#b9d2c7");
viewer.scene.globe.enableLighting = false;
viewer.scene.globe.showGroundAtmosphere = true;
viewer.scene.fog.enabled = true;
viewer.scene.fog.density = 0.00012;
viewer.scene.skyBox.show = false;
viewer.scene.sun.show = false;
viewer.scene.moon.show = false;
viewer.scene.skyAtmosphere.hueShift = -0.02;
viewer.scene.skyAtmosphere.saturationShift = -0.4;
viewer.scene.skyAtmosphere.brightnessShift = 0.3;
viewer.scene.screenSpaceCameraController.minimumZoomDistance = 180_000;
viewer.scene.screenSpaceCameraController.maximumZoomDistance = 8_000_000;

const markerEntries = [];
const entityToRegion = new Map();
let activeMeasure = "total";
let selectedRegion = null;

function markerSize(value, maximum) {
  return 10 + Math.sqrt(value / maximum) * 13;
}

for (const region of baseline.regions) {
  const [longitude, latitude] = centers[region.voivodeship];
  const entity = viewer.entities.add({
    id: `region-${regionKey(region.voivodeship)}`,
    position: Cartesian3.fromDegrees(longitude, latitude, 18_000),
    point: {
      color: Color.fromCssColorString(measures.total.color).withAlpha(0.9),
      outlineColor: Color.fromCssColorString("#f8fbf8"),
      outlineWidth: 3,
      pixelSize: 18,
      scaleByDistance: new NearFarScalar(350_000, 1.15, 3_400_000, 0.72),
      disableDepthTestDistance: 2_400_000,
    },
    label: {
      text: region.voivodeship,
      show: false,
      font: "600 12px Segoe UI",
      fillColor: Color.fromCssColorString("#173b32"),
      outlineColor: Color.fromCssColorString("#f8fbf8"),
      outlineWidth: 5,
      style: LabelStyle.FILL_AND_OUTLINE,
      pixelOffset: new Cartesian2(0, -25),
      distanceDisplayCondition: new DistanceDisplayCondition(0, 2_700_000),
      scaleByDistance: new NearFarScalar(350_000, 1.0, 2_700_000, 0.82),
      disableDepthTestDistance: 2_400_000,
    },
  });

  markerEntries.push({ entity, region, baseSize: 18 });
  entityToRegion.set(entity, region);
}

function setPolandView(duration = 1.3) {
  viewer.camera.flyTo({
    destination: Cartesian3.fromDegrees(19.2, 51.85, 2_200_000),
    orientation: { heading: 0, pitch: CesiumMath.toRadians(-88), roll: 0 },
    duration,
  });
}

function updateMarkerAppearance() {
  const definition = measures[activeMeasure];
  const maximum = Math.max(...baseline.regions.map(definition.value));
  document.documentElement.style.setProperty("--map-accent", definition.color);
  document.querySelector("#measure-label").textContent = definition.label;

  for (const entry of markerEntries) {
    const value = definition.value(entry.region);
    const isSelected = selectedRegion?.voivodeship === entry.region.voivodeship;
    entry.baseSize = markerSize(value, maximum);
    entry.entity.point.pixelSize = entry.baseSize + (isSelected ? 5 : 0);
    entry.entity.point.color = Color.fromCssColorString(definition.color).withAlpha(isSelected ? 1 : 0.88);
    entry.entity.point.outlineColor = Color.fromCssColorString(isSelected ? "#183f35" : "#f8fbf8");
    entry.entity.point.outlineWidth = isSelected ? 4 : 3;
    entry.entity.label.show = isSelected;
    entry.entity.label.text = `${entry.region.voivodeship} · ${format.format(value)}`;
  }

  for (const row of document.querySelectorAll(".region-row")) {
    const region = regionByName.get(row.dataset.region);
    row.querySelector(".region-total").textContent = format.format(definition.value(region));
  }

  viewer.scene.requestRender();
}

function sourceStatus(source) {
  if (source.freshnessStatus === "current" && source.parseStatus.startsWith("parsed")) {
    return { className: "ready", label: "Current public register normalized" };
  }
  if (source.parseStatus.startsWith("parsed")) {
    return { className: "partial", label: "Public register normalized · refresh required" };
  }
  return {
    className: "review",
    label: `Official source located · ${source.freshnessStatus.replaceAll("_", " ")}`,
  };
}

function renderSelection(region) {
  const source = region ? sourceByRegion.get(regionKey(region.voivodeship)) : null;
  const national = baseline.national;
  const total = region ? region.familyCareChildren + region.institutionalResidents : totalChildren;
  const family = region ? region.familyCareChildren : national.familyCareChildren;
  const institution = region ? region.institutionalResidents : national.institutionalResidents;
  const facilities = region ? region.institutionalFacilities : national.institutionalFacilities;
  const institutionalShare = (institution / total) * 100;

  document.querySelector("#selection-kicker").textContent = region ? "Voivodeship selected" : "National view";
  document.querySelector("#place-title").textContent = region?.voivodeship ?? "Poland";
  document.querySelector("#selected-total").textContent = format.format(total);
  document.querySelector("#selected-family").textContent = format.format(family);
  document.querySelector("#selected-institution").textContent = format.format(institution);
  document.querySelector("#selected-facilities").textContent = format.format(facilities);
  document.querySelector("#clear-selection").hidden = !region;
  document.querySelector("#selection-summary").textContent = region
    ? `${institutionalShare.toFixed(1)}% of children in care are in institutional care. The marker is placed at the regional centroid, not at a facility or household.`
    : "Choose a region to inspect its care mix, facility count, and official register status.";

  const statusElement = document.querySelector("#source-status");
  if (source) {
    const status = sourceStatus(source);
    statusElement.innerHTML = `
      <span class="status-dot ${status.className}" aria-hidden="true"></span>
      <div>
        <small>Official register coverage</small>
        <strong>${status.label}</strong>
        <a href="${source.sourceUrl}" target="_blank" rel="noreferrer">${source.authority} ↗</a>
      </div>
    `;
  } else {
    statusElement.innerHTML = `
      <span class="status-dot review" aria-hidden="true"></span>
      <div><small>Official register coverage</small><strong>${coverage.currentRegisterReconciliationCompleteCount} of ${coverage.voivodeshipsTotal} current registers normalized</strong></div>
    `;
  }
}

function selectRegion(region, fly = true) {
  selectedRegion = region;
  for (const row of document.querySelectorAll(".region-row")) {
    const active = region?.voivodeship === row.dataset.region;
    row.classList.toggle("active", active);
    row.setAttribute("aria-pressed", String(active));
  }
  renderSelection(region);
  updateMarkerAppearance();

  if (fly && region) {
    const [longitude, latitude] = centers[region.voivodeship];
    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(longitude, latitude, 1_250_000),
      orientation: { heading: 0, pitch: CesiumMath.toRadians(-88), roll: 0 },
      duration: 1.15,
    });
  }
}

const regionList = document.querySelector("#region-list");
const sortedRegions = [...baseline.regions].sort(
  (a, b) => b.familyCareChildren + b.institutionalResidents - (a.familyCareChildren + a.institutionalResidents),
);

for (const [index, region] of sortedRegions.entries()) {
  const source = sourceByRegion.get(regionKey(region.voivodeship));
  const status = sourceStatus(source);
  const button = document.createElement("button");
  button.className = "region-row";
  button.type = "button";
  button.dataset.region = region.voivodeship;
  button.setAttribute("aria-pressed", "false");
  button.innerHTML = `
    <span class="region-rank">${String(index + 1).padStart(2, "0")}</span>
    <span class="region-name"><strong>${region.voivodeship}</strong><small>${region.institutionalFacilities} institutional facilities</small></span>
    <span class="region-total">${format.format(measures.total.value(region))}</span>
    <span class="status-dot ${status.className}" aria-hidden="true"></span>
  `;
  button.addEventListener("click", () => selectRegion(region));
  regionList.append(button);
}

for (const button of document.querySelectorAll(".measure")) {
  button.setAttribute("aria-pressed", String(button.classList.contains("active")));
  button.addEventListener("click", () => {
    activeMeasure = button.dataset.measure;
    for (const item of document.querySelectorAll(".measure")) {
      const active = item === button;
      item.classList.toggle("active", active);
      item.setAttribute("aria-pressed", String(active));
    }
    updateMarkerAppearance();
  });
}

const mapInteraction = new ScreenSpaceEventHandler(viewer.scene.canvas);
mapInteraction.setInputAction((event) => {
  const picked = viewer.scene.pick(event.position);
  const region = picked?.id ? entityToRegion.get(picked.id) : null;
  if (region) selectRegion(region);
}, ScreenSpaceEventType.LEFT_CLICK);

document.querySelector("#home-view").addEventListener("click", () => {
  selectRegion(null, false);
  setPolandView();
});
document.querySelector("#clear-selection").addEventListener("click", () => {
  selectRegion(null, false);
  setPolandView();
});
document.querySelector("#zoom-in").addEventListener("click", () => viewer.camera.zoomIn(viewer.camera.positionCartographic.height * 0.24));
document.querySelector("#zoom-out").addEventListener("click", () => viewer.camera.zoomOut(viewer.camera.positionCartographic.height * 0.28));

const researchPanel = document.querySelector("#research-panel");
function setResearch(open) {
  researchPanel.classList.toggle("visible", open);
  researchPanel.setAttribute("aria-hidden", String(!open));
  document.body.classList.toggle("modal-open", open);
}
document.querySelector("#method-button").addEventListener("click", () => setResearch(true));
document.querySelector("#close-research").addEventListener("click", () => setResearch(false));
researchPanel.addEventListener("click", (event) => {
  if (event.target === researchPanel) setResearch(false);
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") setResearch(false);
});

const parseStatusLabel = {
  parsed_html: "parsed HTML",
  parsed_pdf: "parsed PDF",
  count_only: "count only",
  binary_unavailable: "binary unavailable",
  landing_only: "landing page only",
  landing_unreachable: "locator unresolved",
  not_parsed: "wrong attachment scope",
};

document.querySelector("#source-table").innerHTML = `
  <div class="source-table-head"><span>Province</span><span>Official authority</span><span>Public source state</span></div>
  ${facilityManifest.sources
    .map(
      (source) => `<a class="source-row" href="${source.sourceUrl}" target="_blank" rel="noreferrer">
        <strong>${source.voivodeship}</strong>
        <span>${source.authority}</span>
        <span class="source-state ${source.freshnessStatus === "current" ? "current" : ""}">${source.freshnessStatus} · ${parseStatusLabel[source.parseStatus] ?? source.parseStatus}</span>
      </a>`,
    )
    .join("")}
`;

let priorityIndex = 0;
function renderPriority() {
  const item = assistance.priorities[priorityIndex];
  document.querySelector("#priority-title").textContent = item.theme;
  document.querySelector("#priority-detail").textContent = item.firstAssistance;
  document.querySelector("#priority-index").textContent = `${String(priorityIndex + 1).padStart(2, "0")} / ${String(assistance.priorities.length).padStart(2, "0")}`;
}
document.querySelector("#priority-next").addEventListener("click", () => {
  priorityIndex = (priorityIndex + 1) % assistance.priorities.length;
  renderPriority();
});

function finishLoading() {
  const loadingCard = document.querySelector("#loading-card");
  if (!loadingCard) return;
  loadingCard.classList.add("complete");
  window.setTimeout(() => loadingCard.remove(), 350);
}

viewer.scene.globe.tileLoadProgressEvent.addEventListener((remaining) => {
  if (remaining === 0) window.setTimeout(finishLoading, 200);
});
window.setTimeout(finishLoading, 2_000);

document.querySelector("#total-children").textContent = format.format(totalChildren);
document.querySelector("#family-count").textContent = format.format(baseline.national.familyCareChildren);
document.querySelector("#institution-count").textContent = format.format(baseline.national.institutionalResidents);
document.querySelector("#facility-count").textContent = `${format.format(baseline.national.institutionalFacilities)} facilities`;
document.querySelector("#normalized-record-count").textContent = format.format(coverage.recordCount);
document.querySelector("#current-register-count").textContent = `${coverage.currentRegisterReconciliationCompleteCount} / ${coverage.voivodeshipsTotal}`;

renderPriority();
renderSelection(null);
updateMarkerAppearance();
setPolandView(0);
